/**
 * @module StateActionValidator
 * @description Provides validation logic for state management actions based on predefined action types and schemas.
 * Ensures that actions dispatched to the state management system are well-formed, use valid paths,
 * have appropriate payloads according to the action type and schema definition, and respect schema constraints
 * like type, enums, required properties, and allowed action types per path.
 * It performs deep validation for object and array structures defined in the schemas.
 */

/**
 * Allowed action type prefixes and their validation rules.
 * Defines the standard prefixes actions should use (e.g., 'SET_', 'ADD_', 'TOGGLE_')
 * and associated rules for payload requirements and basic format validation.
 * @constant {Object.<string, {prefix: string, description: string, requiresPayload: boolean, validatePayload: function(*): boolean}>}
 */
export const ALLOWED_ACTION_TYPES = {
    GET: {
        prefix: 'GET_',
        description: 'For retrieving state without modification',
        requiresPayload: false,
        validatePayload: () => true
    },
    SET: {
        prefix: 'SET_',
        description: 'For completely overwriting state values',
        requiresPayload: true,
        validatePayload: (payload) => payload !== undefined // Allows null, objects, arrays, primitives
    },
    ADD: {
        prefix: 'ADD_',
        description: 'For adding items to arrays or collections',
        requiresPayload: true,
        // Typically expects an object or an array of objects/items to add
        validatePayload: (payload) => typeof payload === 'object' || Array.isArray(payload)
    },
    UPDATE: {
        prefix: 'UPDATE_',
        description: 'For partial updates to existing state values (typically objects)',
        requiresPayload: true,
        validatePayload: (payload) => typeof payload === 'object' && !Array.isArray(payload) && payload !== null
    },
    REMOVE: {
        prefix: 'REMOVE_',
        description: 'For removing items from arrays or collections (payload might be an ID, index, or filter criteria)',
        requiresPayload: true,
        validatePayload: (payload) => payload !== undefined // Can be various types depending on removal logic
    },
    CLEAR: {
        prefix: 'CLEAR_',
        description: 'For resetting state (or parts of it) to initial/empty values',
        requiresPayload: false, // Sometimes might take a payload for targeted clears, adjust if needed
        validatePayload: () => true
    },
    TOGGLE: {
        prefix: 'TOGGLE_',
        description: 'For switching boolean state values',
        requiresPayload: false, // Usually no payload needed to just toggle
        validatePayload: (payload) => payload === undefined // Payload typically not expected
    }
    // Add other custom action type prefixes if necessary
};

/**
 * Finds a schema node by path
 * @param {string} path - The path to the schema node
 * @param {Object} schemas - Combined object of state schemas
 * @returns {Object|null} The schema node or null if not found
 */
const _findSchemaNode = (path, schemas) => {
    if (!path || !schemas) return null;

    // Handle page-specific paths (format: pageName.path)
    const segments = path.split('.');
    const [pageOrContext, ...restPath] = segments;

    // Check if it's a page-specific path
    if (schemas[pageOrContext]) {
        return navigateSchema(schemas[pageOrContext], restPath);
    }

    // Try direct navigation in case it's app-level state
    return navigateSchema(schemas, segments);
};

/**
 * Navigates a schema object using path segments
 * @param {Object} schema - The schema to navigate
 * @param {Array} pathSegments - Array of path segments
 * @returns {Object|null} The schema node or null if not found
 */
const navigateSchema = (schema, pathSegments) => {
    if (!schema || !pathSegments || pathSegments.length === 0) return schema;

    let current = schema;
    for (const segment of pathSegments) {
        if (!current[segment]) {
            // Check if properties contains this segment
            if (current.properties && current.properties[segment]) {
                current = current.properties[segment];
            } else {
                return null;
            }
        } else {
            current = current[segment];
        }
    }

    return current;
};

/**
 * Validates a value against schema type definition, including deep validation for objects and arrays.
 * Assumes schemaNodeOverride is provided for the relevant value being checked.
 * @param {string} path - The state path (used primarily for error messages)
 * @param {*} value - The value to validate
 * @param {Object} schemas - Combined object of state schemas (used for potential nested lookups if needed, though ideally avoided)
 * @param {Object} logger - Logger instance
 * @param {boolean} [isRecursiveCall=false] - Flag to indicate internal recursive call
 * @param {Object|null} schemaNode - The schema node corresponding to the value being validated. REQUIRED.
 */
const validateStateValue = (path, value, schemas, logger, isRecursiveCall = false, schemaNode = null) => {
    // Validation logic when schema node is not found or not provided
    // This should ideally not happen if called correctly from validateAction or recursively
    if (!schemaNode) {
        if (!isRecursiveCall) {
            // If it's the initial call and no schema node was passed, it's an internal error or incorrect usage.
            // We could try to look it up again, but better to enforce the pattern.
            logger.error("validateStateValue called without a schemaNode for initial validation.", null, { path });
            throw new Error(`Schema validation internal error: No schema node provided for path ${path}`);
        }
        // If schema node not found during recursion (e.g., property missing in schema definition):
        if (value !== undefined) {
             logger.warn(`Schema node not found during recursive validation, but value exists.`, null, { path, value });
        } else {
             logger.debug('Schema node not found during recursive validation for undefined value (optional property/item).', null, { path });
        }
        return; // Stop validation for this branch
    }

    // Get the type from the schema node
    const type = schemaNode.type;

    // Validation logic when type is not defined in schema node
    if (!type) {
         if (value === undefined || value === null) { // Allow missing type for undefined/null values
             logger.debug('Value is null or undefined, skipping type check as type is missing in schema.', null, { path });
             // Still check enums if applicable? Unlikely for missing type.
             // Proceed to structure validation if needed (e.g. object/array with no defined type?) - less likely
             // return; // Exit early? Or let structure validation handle it? Let structure handle.
         } else {
             // If value exists but type doesn't, that's usually a schema definition issue.
            throw new Error(`No type defined in schema for path: ${path}`);
         }
    }

    const types = type ? (Array.isArray(type) ? type : [type]) : [];

    // Type validation logic (only if type is defined)
    let isValid = false;
    if (types.length > 0) {
        isValid = types.some(t => {
            if (t === 'any') return true; // 'any' type accepts all values
            if (t === 'null') return value === null;
            // Allow undefined only if 'null' is a possible type or during recursion (optional properties)
            if (value === undefined) return types.includes('null') || isRecursiveCall;
            if (value === null) return true; // Handled by t === 'null' check
            if (t === 'array') return Array.isArray(value);
            if (t === 'object') return typeof value === 'object' && !Array.isArray(value) && value !== null;
            return typeof value === t;
        });

        if (!isValid) {
            const valueType = value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value;
            throw new Error(`Invalid value type for path ${path}. Expected ${types.join(' or ')}, got ${valueType}`);
        }
    } else if (value !== undefined && value !== null) {
        // If no type defined, but value exists (and is not null), what should happen?
        // Current logic allows it. Could add a strict mode later.
        logger.debug(`Value exists for path ${path} but no type is defined in schema. Allowing.`, null, { path, value });
    }


    // Enum validation (only if type validation passed or wasn't needed, and enum exists)
    if (schemaNode.enum && value !== null && value !== undefined) {
        // Ensure the type is valid before checking enum, or if type wasn't defined but value exists.
        if ((isValid || types.length === 0) && !schemaNode.enum.includes(value)) {
            throw new Error(`Value '${value}' for path ${path} must be one of: ${schemaNode.enum.join(', ')}`);
        }
    }

    // --- Deep validation for objects ---
    // Check based on actual value type, not just schema type, as schema might allow multiple types.
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        if (schemaNode.properties) {
            // Validate properties defined in the schema
            for (const key in schemaNode.properties) {
                const subPath = `${path}.${key}`;
                const subValue = value ? value[key] : undefined;
                const subSchemaNode = schemaNode.properties[key]; // Get the specific sub-schema
                // Recursively validate this property
                validateStateValue(subPath, subValue, schemas, logger, true, subSchemaNode);
            }
            // Optional: Check for extra properties in value not in schema
            // Optional: Check for required properties
        }
        // Consider adding check: if schemaNode type is 'object' but 'properties' is missing.
    }

    // --- Deep validation for arrays ---
    // Check based on actual value type
    if (Array.isArray(value)) {
        if (schemaNode.items) {
             // Ensure the schema type actually allows array if we got here
             if (!types.includes('array')) {
                 // This case should technically be caught by type validation earlier if type was defined
                 // But if type allows ['object', 'array'], we need this check.
                 logger.warn(`Value is an array, but schema type might not explicitly be 'array'. Proceeding with item validation based on 'items' definition.`, null, { path, schemaTypes: types });
             }
            // Skip per-item validation when the items schema is a bare `{ type: 'object' }`
            // with no properties to validate. Iterating 100+ rows just to confirm each is an
            // object is pure overhead and causes O(n) cost on every setState for table rows.
            const itemSchema = schemaNode.items;
            const hasItemProperties = itemSchema.properties && Object.keys(itemSchema.properties).length > 0;
            if (hasItemProperties) {
                // Validate each item in the array against the 'items' schema
                value.forEach((element, index) => {
                    const itemPath = `${path}[${index}]`;
                    // Recursively validate the array element
                    validateStateValue(itemPath, element, schemas, logger, true, itemSchema);
                });
            }
            // else: items schema has no properties — type check at array level is sufficient
        } else if (types.includes('array')) {
            // If schema type is array but no 'items' defined, skip deep validation.
             logger.debug(`Array validation for path ${path} skipped: Schema is 'array' but does not define 'items' structure.`, null, { path });
        }
         // If value is array but schema type doesn't include array, type validation should have caught it.
    }

    // Only log success for the initial call
    if (!isRecursiveCall) {
      // Avoid JSON.stringify on potentially large values (e.g., 100+ row arrays) — it causes
      // O(n) serialization cost on every setState call, leading to severe performance degradation.
      const valueSummary = Array.isArray(value)
        ? `Array(${value.length})`
        : (typeof value === 'object' && value !== null)
          ? `Object(${Object.keys(value).length} keys)`
          : String(value);
      logger.debug('State value validated successfully', null, { path, valueSummary });
    }
};

/**
 * Validates an action before it's processed by the reducer
 * @param {Object} action - The action to validate
 * @param {Object} logger - Logger instance
 * @param {Object} schemas - Combined object of state schemas for validation
 */
const validateAction = (action, logger, schemas) => {
    if (!action) throw new Error('Action must be defined');
    if (!action.type) throw new Error('Action must have a type');

    // Find matching action type definition
    const actionTypeKey = Object.keys(ALLOWED_ACTION_TYPES).find(key =>
        action.type.startsWith(ALLOWED_ACTION_TYPES[key].prefix)
    );
    if (!actionTypeKey) throw new Error(`Invalid action type: ${action.type}`);

    const actionType = ALLOWED_ACTION_TYPES[actionTypeKey];
    const { path, payload } = action; // Destructure for clarity

    // Validate payload requirements and format based on action type prefix
    const isToggleAction = actionTypeKey === 'TOGGLE';
    if (actionType.requiresPayload && payload === undefined) {
         throw new Error(`Action type ${action.type} requires a payload`);
    }
    if (!actionType.requiresPayload && payload !== undefined && !isToggleAction) {
         throw new Error(`Action type ${action.type} should not have a payload`);
    }
    if (payload !== undefined && !actionType.validatePayload(payload)) {
        throw new Error(`Invalid payload format for action type ${action.type}`);
    }

    // --- Schema-based validation (if path and schemas are present) ---
    if (schemas && path) {
        // 1. Find the schema node for the path ONCE using navigateSchema on the provided slice
        const pathSegments = path ? path.split('.') : [];
        const schemaNode = navigateSchema(schemas, pathSegments); // Use navigateSchema on the provided slice
        if (!schemaNode) {
            throw new Error(`Invalid state path: ${path}`);
        }

        // 2. Validate payload value/structure against the found schema node
        //    Pass the found schemaNode as the override for the initial call.
        if (payload !== undefined) {
            validateStateValue(path, payload, schemas, logger, false, schemaNode);
        }
        // else: No payload to validate against the schema structure

        // 3. Check if action type prefix is allowed for this path (if defined in schema)
        if (schemaNode.allowedActions && !schemaNode.allowedActions.includes(actionTypeKey)) {
            throw new Error(`Action type ${actionTypeKey} is not allowed for path ${action.path}`);
        }

    } else if (path && !schemas) {
        // Log a warning if path is provided but schemas are not (validation skipped)
        logger.warn('Schema validation skipped for path because schemas were not provided.', null, { action });
    }
    // else: No path provided, so no schema validation possible/needed.

    logger.debug('Action validated successfully', null, { actionType: action.type, path: action.path });
};

// Export the public API
export const StateActionValidator = {
    validateAction
};